import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { 
  User, MapPin, Calendar, CreditCard, FileText, LogOut, Clock, Package,
  CheckCircle2, AlertCircle, ExternalLink, Download, X, Plane, Bus, Users, Bed, Home, AlertTriangle,
  Smartphone, Copy, Eye, Edit2, Save
} from 'lucide-react';
import axios from 'axios';
import { translateReservationStatus } from '../../utils/statusLabels';
import { maskBRDate, isValidBRDate } from '../../utils/brDate';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [partnership, setPartnership] = useState({ has_partnership: false, coupons: [], commissions: [], summary: null });
  const [creditBags, setCreditBags] = useState({ normal: { balance: 0 }, limited: { balance: 0 }, transactions: [] });
  const [partnerPix, setPartnerPix] = useState({ has_pix: false, pix_key: '', pix_key_type: 'cpf', pix_holder_name: '', pix_holder_document: '' });
  const [pixEditing, setPixEditing] = useState(false);
  const [pixSaving, setPixSaving] = useState(false);
  const [partnerPayments, setPartnerPayments] = useState([]);
  const [pixMsg, setPixMsg] = useState(null);

  useEffect(() => {
    const customerData = localStorage.getItem('customerData');
    if (!customerData) {
      navigate('/cliente/login');
      return;
    }
    const parsed = JSON.parse(customerData);
    setCustomer(parsed);
    loadReservations(parsed.cpf);
    loadPartnership(parsed.cpf);
    loadCreditBags(parsed.cpf);
    
    // Polling para atualizar status de pagamentos PIX a cada 30 segundos
    const interval = setInterval(() => {
      loadReservations(parsed.cpf);
    }, 30000);
    
    return () => clearInterval(interval);
  }, [navigate]);

  const loadCreditBags = async (cpf) => {
    try {
      const c = (cpf || '').replace(/\D/g, '');
      if (c.length !== 11) return;
      const r = await axios.get(`${BACKEND_URL}/api/credit-bags/${c}`);
      setCreditBags(r.data);
    } catch (e) {
      console.error('Erro credit bags', e);
    }
  };

  const loadReservations = async (cpf) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/customers/my-reservations/${cpf}`);
      setReservations(response.data);
    } catch (error) {
      console.error('Erro ao carregar reservas:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPartnership = async (cpf) => {
    try {
      const cpfClean = (cpf || '').replace(/\D/g, '');
      if (!cpfClean) return;
      const res = await axios.get(`${BACKEND_URL}/api/partner/commissions/${cpfClean}`);
      setPartnership(res.data);
      // Se é parceiro, carrega PIX + histórico
      if (res.data.has_partnership) {
        try {
          const [pix, pays] = await Promise.all([
            axios.get(`${BACKEND_URL}/api/commission-payments/partner-pix/${cpfClean}`),
            axios.get(`${BACKEND_URL}/api/commission-payments/partner/${cpfClean}`),
          ]);
          if (pix.data.has_pix) setPartnerPix({ ...partnerPix, ...pix.data });
          setPartnerPayments(pays.data.payments || []);
        } catch (e) { /* ignore */ }
      }
    } catch (error) {
      console.error('Erro ao carregar comissões do parceiro:', error);
    }
  };

  const savePartnerPix = async () => {
    if (!partnerPix.pix_key || !partnerPix.pix_holder_name) {
      setPixMsg({ type: 'error', text: 'Preencha chave PIX e nome do titular.' });
      return;
    }
    setPixSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/commission-payments/partner-pix`, {
        partner_cpf: customer.cpf.replace(/\D/g, ''),
        pix_key: partnerPix.pix_key,
        pix_key_type: partnerPix.pix_key_type,
        pix_holder_name: partnerPix.pix_holder_name,
        pix_holder_document: partnerPix.pix_holder_document,
      });
      setPixMsg({ type: 'success', text: 'Dados PIX salvos.' });
      setPixEditing(false);
      setPartnerPix({ ...partnerPix, has_pix: true });
    } catch (e) {
      setPixMsg({ type: 'error', text: e.response?.data?.detail || 'Erro ao salvar.' });
    } finally { setPixSaving(false); }
  };

  const downloadPartnerProof = async (paymentId) => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/commission-payments/proof/${paymentId}`);
      const link = document.createElement('a');
      link.href = `data:application/octet-stream;base64,${r.data.base64}`;
      link.download = r.data.filename || `comprovante-${paymentId}.bin`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) { setPixMsg({ type: 'error', text: 'Erro ao baixar comprovante.' }); }
  };

  const handleLogout = () => {
    localStorage.removeItem('customerData');
    localStorage.removeItem('customerToken');
    navigate('/cliente/login');
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price || 0);
  };

  const getStatusBadge = (status, paymentStatus) => {
    if (paymentStatus === 'paid' || paymentStatus === 'RECEIVED' || paymentStatus === 'CONFIRMED' || status === 'confirmed' || status === 'paid') {
      return <Badge className="bg-green-100 text-green-700">Confirmado</Badge>;
    }
    if (paymentStatus === 'OVERDUE') {
      return <Badge className="bg-red-100 text-red-700">Atrasado</Badge>;
    }
    if (status === 'pending' || paymentStatus === 'PENDING') {
      return <Badge className="bg-yellow-100 text-yellow-700">Pendente</Badge>;
    }
    // Traduz demais status técnicos
    const label = translateReservationStatus(status);
    const cls = status === 'cancellation_requested' ? 'bg-orange-100 text-orange-800'
      : status === 'awaiting_contract_acceptance' ? 'bg-amber-100 text-amber-800'
      : status === 'cancelled_transferred_to_credit' ? 'bg-teal-100 text-teal-800'
      : status === 'cancelled_by_agency' || status === 'cancelled_by_customer' || status === 'cancelled' ? 'bg-red-100 text-red-700'
      : 'bg-gray-100 text-gray-700';
    return <Badge className={cls}>{label}</Badge>;
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      'pix': 'PIX',
      'boleto': 'Boleto',
      'credit_card': 'Cartão de Crédito'
    };
    return labels[method] || method || '-';
  };

  const getRoomTypeLabel = (type) => {
    const labels = {
      'compartilhado': 'Quarto Compartilhado',
      'casal': 'Quarto de Casal',
      'privativo': 'Quarto Privativo',
      'individual': 'Quarto Individual'
    };
    return labels[type] || type || '-';
  };

  // Função auxiliar para verificar se é viagem aérea
  const isAirTravel = (category) => {
    if (!category) return false;
    const cat = category.toLowerCase();
    return cat.includes('aereo') || cat.includes('aéreo') || cat === 'internacional';
  };

  // Estado para copiar PIX
  const [copiedPix, setCopiedPix] = useState(null);

  // Estados para edição de dados
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMessage, setProfileMessage] = useState(null);

  // Inicializar dados de edição quando customer carrega
  useEffect(() => {
    if (customer) {
      setEditData({
        full_name: customer.full_name || '',
        email: customer.email || '',
        phone: customer.phone || '',
        birth_date: customer.birth_date || '',
        rg: customer.rg || '',
        passport: customer.passport || '',
        gender: customer.gender || ''
      });
    }
  }, [customer]);

  // Função para salvar dados do cliente
  const handleSaveProfile = async () => {
    if (!customer?.cpf) return;
    
    setSavingProfile(true);
    setProfileMessage(null);
    
    try {
      const response = await axios.put(`${BACKEND_URL}/api/customers/me/${customer.cpf}`, editData);
      
      if (response.data.success) {
        // Atualizar localStorage com novos dados
        const updatedCustomer = { ...customer, ...editData };
        localStorage.setItem('customerData', JSON.stringify(updatedCustomer));
        setCustomer(updatedCustomer);
        setIsEditing(false);
        setProfileMessage({ type: 'success', text: 'Dados atualizados com sucesso!' });
      }
    } catch (error) {
      console.error('Erro ao atualizar dados:', error);
      setProfileMessage({ type: 'error', text: error.response?.data?.detail || 'Erro ao atualizar dados.' });
    } finally {
      setSavingProfile(false);
    }
  };

  // Formatar telefone
  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '').slice(0, 11);
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
  };

  // Filtrar reservas com boletos (pendentes ou pagos)
  // Verificar tanto payment_method quanto existência de boletos
  const reservationsWithBoletos = reservations.filter(r => 
    (r.payment_method === 'boleto' || r.boletos?.length > 0 || r.boleto_url)
  );

  // Filtrar reservas com PIX pendente
  const reservationsWithPix = reservations.filter(r => 
    r.payment_method === 'pix' && r.pix_qr_code && r.pix_status !== 'RECEIVED' && r.pix_status !== 'CONFIRMED'
  );

  // Filtrar reservas manuais com pagamento PIX/INTER pendente
  const manualReservationsWithPendingPayment = reservations.filter(r => 
    (r.is_manual_reservation || r.is_manual) && 
    r.payment_method !== 'credit_card' &&
    r.payment_method !== 'cartao' &&
    (r.amount_paid || 0) < (r.total_amount || 0)
  );

  // QR Code PIX INTER da empresa
  const INTER_PIX_QRCODE = '/pix-inter-qrcode.png';
  const INTER_PIX_KEY = '46.231.427/0001-00'; // CNPJ

  // Função para copiar código PIX
  const copyPixCode = async (pixCode, resId) => {
    try {
      await navigator.clipboard.writeText(pixCode);
      setCopiedPix(resId);
      setTimeout(() => setCopiedPix(null), 3000);
    } catch (err) {
      console.error('Erro ao copiar:', err);
    }
  };

  // Função para calcular contagem regressiva
  const getCountdown = (dateString) => {
    if (!dateString) return null;
    
    // Extrair a primeira data do string (ex: "17 a 23 de Abril/2026" -> 17/04/2026)
    const months = {
      'janeiro': 0, 'fevereiro': 1, 'março': 2, 'abril': 3,
      'maio': 4, 'junho': 5, 'julho': 6, 'agosto': 7,
      'setembro': 8, 'outubro': 9, 'novembro': 10, 'dezembro': 11
    };
    
    const match = dateString.match(/(\d+)\s*(?:a\s*\d+\s*)?de\s*(\w+)\/(\d{4})/i);
    if (!match) return null;
    
    const day = parseInt(match[1]);
    const monthName = match[2].toLowerCase();
    const year = parseInt(match[3]);
    const month = months[monthName];
    
    if (month === undefined) return null;
    
    const tripDate = new Date(year, month, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const diffTime = tripDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return { days: 0, label: 'Viagem realizada', past: true };
    if (diffDays === 0) return { days: 0, label: 'Hoje!', thisWeek: true };
    if (diffDays <= 7) return { days: diffDays, label: `${diffDays} dia${diffDays > 1 ? 's' : ''}`, thisWeek: true };
    if (diffDays <= 30) return { days: diffDays, label: `${diffDays} dias`, medium: true };
    return { days: diffDays, label: `${diffDays} dias`, far: true };
  };

  // Verificar se boleto está atrasado
  const isBoletoOverdue = (boleto) => {
    if (!boleto.due_date) return false;
    if (boleto.status === 'RECEIVED' || boleto.status === 'CONFIRMED' || boleto.status === 'PAID') return false;
    const dueDate = new Date(boleto.due_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return dueDate < today;
  };

  // Verificar se boleto está pago
  const isBoletoPaid = (boleto) => {
    return boleto.status === 'RECEIVED' || boleto.status === 'CONFIRMED' || boleto.status === 'PAID';
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#003366]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Olá, {customer?.full_name?.split(' ')[0] || customer?.name?.split(' ')[0]}!</h1>
            <p className="text-gray-600">Gerencie suas viagens e pagamentos</p>
          </div>
          <Button variant="outline" onClick={handleLogout} className="mt-4 md:mt-0">
            <LogOut className="w-4 h-4 mr-2" />
            Sair
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4 text-center">
              <Package className="w-8 h-8 text-[#003366] mx-auto mb-2" />
              <p className="text-2xl font-bold">{reservations.length}</p>
              <p className="text-xs text-gray-500">Viagens</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <CheckCircle2 className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-2xl font-bold">
                {reservations.filter(r => r.status === 'confirmed' || r.payment_status === 'paid').length}
              </p>
              <p className="text-xs text-gray-500">Confirmadas</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Clock className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
              <p className="text-2xl font-bold">
                {reservations.filter(r => r.status === 'pending').length}
              </p>
              <p className="text-xs text-gray-500">Pendentes</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <CreditCard className="w-8 h-8 text-orange-600 mx-auto mb-2" />
              <p className="text-2xl font-bold">{reservationsWithBoletos.length + reservationsWithPix.length}</p>
              <p className="text-xs text-gray-500">Pagamentos</p>
            </CardContent>
          </Card>
        </div>

        {/* Bolsa de Crédito — só aparece se tiver saldo */}
        {(creditBags?.normal?.balance > 0 || creditBags?.limited?.balance > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6" data-testid="customer-credit-bags">
            {creditBags?.normal?.balance > 0 && (
              <Card className="border-emerald-200 bg-emerald-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold text-emerald-800 uppercase tracking-wide">Bolsa de Crédito</span>
                    <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Utilização integral</span>
                  </div>
                  <p className="text-2xl font-bold text-emerald-900" data-testid="customer-credit-normal">
                    {new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format(creditBags.normal.balance)}
                  </p>
                  <p className="text-xs text-emerald-700 mt-1">Utilize em qualquer nova compra até o valor total.</p>
                </CardContent>
              </Card>
            )}
            {creditBags?.limited?.balance > 0 && (
              <Card className="border-amber-200 bg-amber-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold text-amber-800 uppercase tracking-wide">Bolsa de Crédito Limitada</span>
                    <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Máx. 30% do valor</span>
                  </div>
                  <p className="text-2xl font-bold text-amber-900" data-testid="customer-credit-limited">
                    {new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format(creditBags.limited.balance)}
                  </p>
                  <p className="text-xs text-amber-700 mt-1">Aplicável até 30% do valor de cada nova compra.</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Alerta de documentação de menores pendente */}
        {(() => {
          const pendingMinors = reservations.filter(r => (r.minor_documents || []).some(d => d.status === 'denied' || d.status === 'pending'));
          const missingMinors = reservations.filter(r => {
            const travelers = r.travelers || r.all_travelers || [];
            const m = (r.selected_date || '').match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
            if (!m) return false;
            const tripD = new Date(parseInt(m[3],10), parseInt(m[2],10)-1, parseInt(m[1],10));
            const hasMinorWithoutDocs = travelers.some((t, i) => {
              const bd = t.birth_date || t.birthDate;
              if (!bd) return false;
              const mm = String(bd).match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
              if (!mm) return false;
              const birthD = new Date(parseInt(mm[3],10), parseInt(mm[2],10)-1, parseInt(mm[1],10));
              let age = tripD.getFullYear() - birthD.getFullYear();
              if (tripD.getMonth() < birthD.getMonth() || (tripD.getMonth() === birthD.getMonth() && tripD.getDate() < birthD.getDate())) age -= 1;
              const hasDocs = (r.minor_documents || []).some(d => d.traveler_index === i);
              return age < 18 && !hasDocs;
            });
            return hasMinorWithoutDocs;
          });
          const rWithIssue = [...pendingMinors, ...missingMinors];
          if (rWithIssue.length === 0) return null;
          return (
            <Card className="mb-6 border-purple-300 bg-purple-50" data-testid="minor-docs-alert">
              <CardContent className="p-4 flex items-center gap-3">
                <FileText className="w-6 h-6 text-purple-700" />
                <div className="flex-1">
                  <p className="font-bold text-purple-900">Documentação de Menor pendente</p>
                  <p className="text-xs text-purple-800">Você tem reservas com menores que precisam de documentos anexados/reenviados. Abra a reserva para regularizar.</p>
                </div>
                <Button
                  onClick={() => setSelectedTrip(rWithIssue[0])}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="review-minor-docs-btn"
                >
                  Abrir reserva
                </Button>
              </CardContent>
            </Card>
          );
        })()}

        {/* Novo contrato disponível para aceite (após transferência) */}
        {reservations.some(r => r.contract_needs_new_acceptance) && (
          <Card className="mb-6 border-amber-300 bg-amber-50" data-testid="new-contract-banner">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-amber-700" />
              <div className="flex-1">
                <p className="font-bold text-amber-900">⚠️ Novo contrato disponível para aceite</p>
                <p className="text-xs text-amber-800">Uma de suas reservas foi alterada e requer aceitação do novo contrato para confirmar a nova contratação.</p>
              </div>
              <Button
                onClick={() => {
                  const r = reservations.find(x => x.contract_needs_new_acceptance);
                  if (r) setSelectedTrip(r);
                }}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="review-new-contract-btn"
              >
                Ver
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs defaultValue="viagens" className="space-y-6">
          <TabsList className={`grid grid-cols-2 ${partnership.has_partnership ? 'sm:grid-cols-5' : 'sm:grid-cols-4'} w-full ${partnership.has_partnership ? 'max-w-2xl' : 'max-w-lg'} mx-auto gap-1`}>
            <TabsTrigger value="viagens" className="text-xs sm:text-sm" data-testid="tab-viagens">Viagens</TabsTrigger>
            <TabsTrigger value="pagamentos" className="text-xs sm:text-sm" data-testid="tab-pagamentos">Pagamentos</TabsTrigger>
            <TabsTrigger value="contratos" className="text-xs sm:text-sm" data-testid="tab-contratos">Contratos</TabsTrigger>
            <TabsTrigger value="dados" className="text-xs sm:text-sm" data-testid="tab-dados">Meus Dados</TabsTrigger>
            {partnership.has_partnership && (
              <TabsTrigger value="comissoes" className="text-xs sm:text-sm bg-emerald-50 data-[state=active]:bg-emerald-600 data-[state=active]:text-white" data-testid="tab-comissoes">Comissões</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="viagens">
            {reservations.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhuma viagem encontrada</h3>
                  <p className="text-gray-600 mb-6">Você ainda não possui viagens reservadas. Que tal explorar nossos destinos incríveis?</p>
                  <Button 
                    onClick={() => navigate('/pacotes')} 
                    className="bg-[#FFD700] hover:bg-[#FFC700] text-gray-900 font-semibold px-8 py-3"
                  >
                    🌎 Explorar Pacotes de Viagem
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {reservations.map((res, index) => {
                  const countdown = getCountdown(res.selected_date);
                  return (
                    <Card 
                      key={res.id || index} 
                      className="hover:shadow-lg transition-shadow cursor-pointer relative overflow-hidden"
                      onClick={() => setSelectedTrip(res)}
                    >
                      {/* Contagem regressiva */}
                      {countdown && !countdown.past && (
                        <div className={`absolute top-0 right-0 px-3 py-1 text-xs font-bold rounded-bl-lg ${
                          countdown.thisWeek ? 'bg-green-500 text-white animate-pulse' :
                          countdown.medium ? 'bg-orange-500 text-white' :
                          'bg-blue-500 text-white'
                        }`}>
                          ⏱️ {countdown.label}
                        </div>
                      )}
                      {countdown?.past && (
                        <div className="absolute top-0 right-0 px-3 py-1 text-xs font-bold rounded-bl-lg bg-blue-500 text-white">
                          ✓ Realizada
                        </div>
                      )}
                      
                      <CardContent className="p-4 pt-6">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            {isAirTravel(res.package_category) ? (
                              <Plane className="w-5 h-5 text-blue-600" />
                            ) : (
                              <Bus className="w-5 h-5 text-green-600" />
                            )}
                            <h3 className="font-semibold text-gray-900">{res.package_name}</h3>
                          </div>
                          {getStatusBadge(res.status, res.payment_status)}
                        </div>
                        
                        <div className="space-y-2 text-sm text-gray-600">
                          <p className="flex items-center"><Calendar className="w-4 h-4 mr-2" />{res.selected_date}</p>
                          <p className="flex items-center"><Users className="w-4 h-4 mr-2" />{res.number_of_people || 1} pessoa(s)</p>
                          <p className="flex items-center"><CreditCard className="w-4 h-4 mr-2" />{getPaymentMethodLabel(res.payment_method)}</p>
                          
                          {/* Dados do viajante principal */}
                          {res.customer_name && (
                            <div className="pt-2 mt-2 border-t border-dashed">
                              <p className="flex items-center text-gray-700">
                                <User className="w-4 h-4 mr-2 text-[#003366]" />
                                <span className="font-medium">{res.customer_name}</span>
                              </p>
                              {res.room_type && (
                                <p className="flex items-center mt-1">
                                  <Bed className="w-4 h-4 mr-2" />
                                  {getRoomTypeLabel(res.room_type)}
                                </p>
                              )}
                              {res.boarding_point && (
                                <p className="flex items-center mt-1">
                                  <MapPin className="w-4 h-4 mr-2" />
                                  {res.boarding_point}
                                </p>
                              )}
                              {res.city_departure && (
                                <p className="flex items-center mt-1">
                                  <MapPin className="w-4 h-4 mr-2" />
                                  Embarque: {res.city_departure === 'RJ' ? 'Rio de Janeiro' : res.city_departure === 'SP' ? 'São Paulo' : res.city_departure}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                        
                        <div className="mt-3 pt-3 border-t flex justify-between items-center">
                          <div>
                            <span className="font-bold text-[#003366]">{formatPrice(res.total_amount)}</span>
                            {/* Mostrar valor pago/faltante para reservas manuais */}
                            {(res.is_manual_reservation || res.is_manual) && res.payment_method !== 'credit_card' && res.payment_method !== 'cartao' && (
                              <div className="text-xs mt-1">
                                {(res.amount_paid || 0) > 0 && (
                                  <span className="text-green-600 mr-2">Pago: {formatPrice(res.amount_paid)}</span>
                                )}
                                {((res.total_amount || 0) - (res.amount_paid || 0)) > 0 && (
                                  <span className="text-orange-600">Falta: {formatPrice((res.total_amount || 0) - (res.amount_paid || 0))}</span>
                                )}
                              </div>
                            )}
                          </div>
                          <span className="text-xs text-blue-600">Clique para detalhes →</span>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="pagamentos">
            {reservationsWithBoletos.length === 0 && reservationsWithPix.length === 0 && manualReservationsWithPendingPayment.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <CreditCard className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum pagamento encontrado</h3>
                  <p className="text-gray-600 mb-6">Você não possui pagamentos pendentes ou registrados.</p>
                  <Button 
                    onClick={() => navigate('/pacotes')} 
                    className="bg-[#FFD700] hover:bg-[#FFC700] text-gray-900 font-semibold"
                  >
                    🌎 Explorar Pacotes de Viagem
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* Seção Pagamentos Pendentes - Reservas Manuais */}
                {manualReservationsWithPendingPayment.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Smartphone className="w-5 h-5 text-orange-600" />
                      Pagamentos Pendentes (PIX)
                    </h3>
                    <div className="space-y-4">
                      {manualReservationsWithPendingPayment.map((res, index) => {
                        const amountPending = (res.total_amount || 0) - (res.amount_paid || 0);
                        return (
                          <Card key={res.id || index} className="border-orange-200 bg-orange-50">
                            <CardContent className="p-4 md:p-6">
                              <div className="flex flex-col gap-4">
                                {/* Header */}
                                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-orange-200 pb-4">
                                  <div>
                                    <h4 className="font-semibold text-gray-900">{res.package_name}</h4>
                                    <p className="text-sm text-gray-600">{res.selected_date}</p>
                                  </div>
                                  <div className="text-right">
                                    <div className="grid grid-cols-3 gap-4 text-center">
                                      <div>
                                        <p className="text-xs text-gray-500">Total</p>
                                        <p className="font-bold text-[#003366]">{formatPrice(res.total_amount)}</p>
                                      </div>
                                      <div>
                                        <p className="text-xs text-gray-500">Pago</p>
                                        <p className="font-bold text-green-600">{formatPrice(res.amount_paid || 0)}</p>
                                      </div>
                                      <div>
                                        <p className="text-xs text-gray-500">Falta</p>
                                        <p className="font-bold text-orange-600">{formatPrice(amountPending)}</p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* QR Code e Chave PIX */}
                                <div className="grid md:grid-cols-2 gap-4">
                                  {/* QR Code */}
                                  <div className="flex flex-col items-center justify-center p-4 bg-white rounded-lg">
                                    <p className="text-sm text-gray-600 mb-2 font-semibold">Escaneie o QR Code:</p>
                                    <img 
                                      src={INTER_PIX_QRCODE}
                                      alt="QR Code PIX INTER"
                                      className="w-48 h-48 border rounded-lg"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'block';
                                      }}
                                    />
                                    <div className="hidden text-center p-4 bg-gray-100 rounded-lg">
                                      <p className="text-sm text-gray-600">QR Code não disponível</p>
                                      <p className="text-xs text-gray-500">Use a chave PIX abaixo</p>
                                    </div>
                                  </div>
                                  
                                  {/* Chave PIX */}
                                  <div className="flex flex-col justify-center p-4 bg-white rounded-lg">
                                    <p className="text-sm text-gray-600 mb-2 font-semibold">Ou use a chave PIX (CNPJ):</p>
                                    <div className="bg-gray-100 p-3 rounded-lg text-center mb-3">
                                      <span className="text-lg font-mono font-bold text-gray-800">{INTER_PIX_KEY}</span>
                                    </div>
                                    <Button
                                      onClick={() => copyPixCode(INTER_PIX_KEY, `manual-${res.id}`)}
                                      className={`w-full ${copiedPix === `manual-${res.id}` ? 'bg-green-600' : 'bg-orange-500 hover:bg-orange-600'}`}
                                    >
                                      {copiedPix === `manual-${res.id}` ? (
                                        <>
                                          <CheckCircle2 className="w-4 h-4 mr-2" />
                                          Copiado!
                                        </>
                                      ) : (
                                        <>
                                          <Copy className="w-4 h-4 mr-2" />
                                          Copiar Chave PIX
                                        </>
                                      )}
                                    </Button>
                                    
                                    <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                                      <p className="text-xs text-yellow-800">
                                        <strong>Importante:</strong> Após o pagamento, envie o comprovante pelo WhatsApp para confirmação.
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                
                                <p className="text-xs text-gray-500 text-center">
                                  Pagamento via PIX para: <strong>TÔ INDO ALI - Turismo e Aventura</strong> (Banco Inter)
                                </p>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Seção PIX Pendentes */}
                {reservationsWithPix.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Smartphone className="w-5 h-5 text-green-600" />
                      PIX Pendentes
                    </h3>
                    <div className="space-y-4">
                      {reservationsWithPix.map((res, index) => (
                        <Card key={res.id || index} className="border-green-200 bg-green-50">
                          <CardContent className="p-4 md:p-6">
                            <div className="flex flex-col gap-4">
                              {/* Header */}
                              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-green-200 pb-4">
                                <div>
                                  <h4 className="font-semibold text-gray-900">{res.package_name}</h4>
                                  <p className="text-sm text-gray-600">{res.selected_date}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm text-gray-500">Valor</p>
                                  <p className="font-bold text-lg text-green-700">{formatPrice(res.pix_value || res.total_amount)}</p>
                                </div>
                              </div>
                              
                              {/* QR Code e Copia/Cola */}
                              <div className="grid md:grid-cols-2 gap-4">
                                {/* QR Code */}
                                <div className="flex flex-col items-center justify-center p-4 bg-white rounded-lg">
                                  <p className="text-sm text-gray-600 mb-2">Escaneie o QR Code:</p>
                                  <img 
                                    src={`data:image/png;base64,${res.pix_qr_code}`}
                                    alt="QR Code PIX"
                                    className="w-48 h-48"
                                  />
                                </div>
                                
                                {/* Copia/Cola */}
                                <div className="flex flex-col justify-center p-4 bg-white rounded-lg">
                                  <p className="text-sm text-gray-600 mb-2">Ou copie o código:</p>
                                  <div className="bg-gray-100 p-3 rounded-lg break-all text-xs text-gray-700 mb-3 max-h-24 overflow-y-auto">
                                    {res.pix_copy_paste}
                                  </div>
                                  <Button
                                    onClick={() => copyPixCode(res.pix_copy_paste, res.id)}
                                    className={`w-full ${copiedPix === res.id ? 'bg-green-600' : 'bg-[#003366]'}`}
                                  >
                                    {copiedPix === res.id ? (
                                      <>
                                        <CheckCircle2 className="w-4 h-4 mr-2" />
                                        Copiado!
                                      </>
                                    ) : (
                                      <>
                                        <Copy className="w-4 h-4 mr-2" />
                                        Copiar Código PIX
                                      </>
                                    )}
                                  </Button>
                                </div>
                              </div>
                              
                              <p className="text-xs text-gray-500 text-center">
                                Após o pagamento, a confirmação pode levar alguns minutos para aparecer.
                              </p>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* Seção Boletos */}
                {reservationsWithBoletos.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-orange-600" />
                      Boletos
                    </h3>
                    <div className="space-y-4">
                {reservationsWithBoletos.map((res, index) => (
                  <Card key={res.id || index}>
                    <CardContent className="p-4 md:p-6">
                      <div className="flex flex-col gap-4">
                        {/* Header da reserva */}
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b pb-4">
                          <div>
                            <h3 className="font-semibold text-gray-900">{res.package_name}</h3>
                            <p className="text-sm text-gray-600">{res.selected_date}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-500">Valor Total</p>
                            <p className="font-bold text-lg text-[#003366]">{formatPrice(res.total_amount)}</p>
                          </div>
                        </div>
                        
                        {/* Lista de boletos (carnê) */}
                        {res.boletos && res.boletos.length > 0 ? (
                          <div className="space-y-3">
                            <p className="text-sm font-medium text-gray-700">
                              {res.boletos.length > 1 ? `Carnê com ${res.boletos.length} boletos:` : 'Boleto:'}
                            </p>
                            {res.boletos.map((boleto, bIdx) => {
                              const isPaid = isBoletoPaid(boleto);
                              const isOverdue = isBoletoOverdue(boleto);
                              
                              return (
                                <div 
                                  key={bIdx} 
                                  className={`p-3 rounded-lg ${
                                    isPaid ? 'bg-green-50 border border-green-200' : 
                                    isOverdue ? 'bg-red-50 border border-red-200' : 
                                    'bg-gray-50'
                                  }`}
                                >
                                  {/* Layout responsivo - empilha no mobile */}
                                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                    <div className="flex items-start gap-3">
                                      <div className="mt-0.5">
                                        {isPaid && <CheckCircle2 className="w-5 h-5 text-green-600" />}
                                        {isOverdue && !isPaid && <AlertTriangle className="w-5 h-5 text-red-600" />}
                                        {!isPaid && !isOverdue && <Clock className="w-5 h-5 text-yellow-600" />}
                                      </div>
                                      
                                      <div className="flex flex-col">
                                        <span className={`font-medium ${isPaid ? 'text-green-800' : isOverdue ? 'text-red-800' : 'text-gray-900'}`}>
                                          {res.boletos.length > 1 ? `Parcela ${boleto.installment}/${res.boletos.length}` : 'Boleto'}
                                          {isPaid && (
                                            <Badge className="ml-2 bg-green-100 text-green-700">PAGO</Badge>
                                          )}
                                        </span>
                                        <span className={`text-sm ${isPaid ? 'text-green-700' : isOverdue ? 'text-red-700' : 'text-gray-600'}`}>
                                          {formatPrice(boleto.value)}
                                        </span>
                                        {boleto.due_date && (
                                          <span className={`text-xs ${isOverdue && !isPaid ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                                            {isOverdue && !isPaid ? 'VENCIDO em ' : 'Vence em '}
                                            {new Date(boleto.due_date).toLocaleDateString('pt-BR')}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {(boleto.boleto_url || boleto.invoice_url) && (
                                      <div className="pl-8 sm:pl-0">
                                        {isPaid ? (
                                          <Button 
                                            size="sm"
                                            variant="outline"
                                            disabled
                                            className="bg-gray-100 text-gray-500 cursor-not-allowed w-full sm:w-auto"
                                          >
                                            <CheckCircle2 className="w-4 h-4 mr-1" />
                                            Pago
                                          </Button>
                                        ) : (
                                          <Button 
                                            size="sm"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              window.open(boleto.boleto_url || boleto.invoice_url, '_blank');
                                            }}
                                            className={`w-full sm:w-auto ${isOverdue ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                                          >
                                            <ExternalLink className="w-4 h-4 mr-1" />
                                            {isOverdue ? 'Pagar Agora' : 'Ver Boleto'}
                                          </Button>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : res.boleto_url ? (
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-600">1 boleto disponível</p>
                            <Button 
                              onClick={() => window.open(res.boleto_url, '_blank')}
                              className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto"
                            >
                              <ExternalLink className="w-4 h-4 mr-2" />
                              Ver Boleto
                            </Button>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">Boleto não disponível no momento.</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="contratos">
            {reservations.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum contrato encontrado</h3>
                  <p className="text-gray-600 mb-6">Seus contratos de viagem aparecerão aqui após realizar uma reserva.</p>
                  <Button 
                    onClick={() => navigate('/pacotes')} 
                    className="bg-[#FFD700] hover:bg-[#FFC700] text-gray-900 font-semibold"
                  >
                    🌎 Explorar Pacotes de Viagem
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {reservations.map((res, index) => {
                  const needsContractAcceptance = (res.contract_needs_new_acceptance) || ((res.is_manual_reservation || res.is_manual) && !res.contract_accepted);
                  
                  return (
                  <Card key={res.id || index} className={needsContractAcceptance ? 'border-yellow-400 border-2' : ''}>
                    <CardContent className="p-4 md:p-6">
                      {/* Alerta de aceite pendente */}
                      {needsContractAcceptance && (
                        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <div className="flex items-center gap-2 text-yellow-800">
                            <AlertTriangle className="w-5 h-5" />
                            <span className="font-medium">Aceite de contrato pendente</span>
                          </div>
                          <p className="text-sm text-yellow-700 mt-1">
                            Esta reserva foi feita manualmente. Por favor, leia e aceite o contrato para confirmar sua participação.
                          </p>
                        </div>
                      )}
                      
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex items-center space-x-4">
                          <div className={`p-3 rounded-lg ${
                            needsContractAcceptance ? 'bg-yellow-100' :
                            res.payment_status === 'RECEIVED' || res.payment_status === 'CONFIRMED' || res.status === 'confirmed' ? 'bg-green-100' : 'bg-yellow-100'
                          }`}>
                            <FileText className={`w-6 h-6 ${
                              needsContractAcceptance ? 'text-yellow-600' :
                              res.payment_status === 'RECEIVED' || res.payment_status === 'CONFIRMED' || res.status === 'confirmed' ? 'text-green-600' : 'text-yellow-600'
                            }`} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{res.package_name}</h3>
                            <p className="text-sm text-gray-600">{res.selected_date}</p>
                            <div className="flex flex-wrap gap-2 mt-1">
                              <Badge className={res.payment_status === 'RECEIVED' || res.payment_status === 'CONFIRMED' || res.status === 'confirmed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                                {res.payment_status === 'RECEIVED' || res.payment_status === 'CONFIRMED' || res.status === 'confirmed' ? 'Pago' : 'Aguardando Pagamento'}
                              </Badge>
                              {res.contract_accepted && (
                                <Badge className="bg-green-100 text-green-700">
                                  <CheckCircle2 className="w-3 h-3 mr-1" />
                                  Contrato Aceito
                                </Badge>
                              )}
                              {needsContractAcceptance && (
                                <Badge className="bg-yellow-100 text-yellow-700">
                                  Aguardando Aceite
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {needsContractAcceptance ? (
                            <Button
                              onClick={() => navigate(`/contrato/${res.id}?aceitar=true`)}
                              className="bg-yellow-500 hover:bg-yellow-600 text-white"
                            >
                              <FileText className="w-4 h-4 mr-2" />
                              Ler e Aceitar Contrato
                            </Button>
                          ) : (
                            <>
                              <Button
                                onClick={() => navigate(`/contrato/${res.id}`)}
                                className="bg-[#003366] hover:bg-[#002244] text-white"
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                Ver Contrato
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => {
                                  window.open(`${BACKEND_URL}/api/contracts/${res.id}/pdf`, '_blank');
                                }}
                                className="border-[#003366] text-[#003366]"
                              >
                                <Download className="w-4 h-4 mr-2" />
                                Baixar
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Tab Meus Dados */}
          <TabsContent value="dados">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <User className="w-6 h-6 text-[#003366]" />
                    <h3 className="text-lg font-semibold text-gray-900">Meus Dados</h3>
                  </div>
                  {!isEditing ? (
                    <Button 
                      onClick={() => setIsEditing(true)}
                      variant="outline"
                      className="border-[#003366] text-[#003366]"
                    >
                      <Edit2 className="w-4 h-4 mr-2" />
                      Editar
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button 
                        onClick={() => {
                          setIsEditing(false);
                          setEditData({
                            full_name: customer?.full_name || '',
                            email: customer?.email || '',
                            phone: customer?.phone || '',
                            birth_date: customer?.birth_date || '',
                            rg: customer?.rg || '',
                            passport: customer?.passport || '',
                            gender: customer?.gender || ''
                          });
                        }}
                        variant="outline"
                      >
                        Cancelar
                      </Button>
                      <Button 
                        onClick={handleSaveProfile}
                        disabled={savingProfile}
                        className="bg-[#003366] hover:bg-[#002244]"
                      >
                        {savingProfile ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2" />
                        ) : (
                          <Save className="w-4 h-4 mr-2" />
                        )}
                        Salvar
                      </Button>
                    </div>
                  )}
                </div>

                {profileMessage && (
                  <div className={`mb-4 p-3 rounded-lg ${profileMessage.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                    {profileMessage.text}
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Nome Completo */}
                  <div>
                    <Label className="text-sm text-gray-600">Nome Completo</Label>
                    {isEditing ? (
                      <Input
                        value={editData.full_name}
                        onChange={(e) => setEditData({ ...editData, full_name: e.target.value })}
                        className="mt-1"
                      />
                    ) : (
                      <p className="font-medium text-gray-900 mt-1">{customer?.full_name || '-'}</p>
                    )}
                  </div>

                  {/* CPF (não editável) */}
                  <div>
                    <Label className="text-sm text-gray-600">CPF</Label>
                    <p className="font-medium text-gray-900 mt-1">{customer?.cpf || '-'}</p>
                    {isEditing && <p className="text-xs text-gray-500">O CPF não pode ser alterado</p>}
                  </div>

                  {/* E-mail */}
                  <div>
                    <Label className="text-sm text-gray-600">E-mail</Label>
                    {isEditing ? (
                      <Input
                        type="email"
                        value={editData.email}
                        onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                        className="mt-1"
                      />
                    ) : (
                      <p className="font-medium text-gray-900 mt-1">{customer?.email || '-'}</p>
                    )}
                  </div>

                  {/* Telefone */}
                  <div>
                    <Label className="text-sm text-gray-600">Telefone</Label>
                    {isEditing ? (
                      <Input
                        value={editData.phone}
                        onChange={(e) => setEditData({ ...editData, phone: formatPhone(e.target.value) })}
                        placeholder="(00) 00000-0000"
                        className="mt-1"
                      />
                    ) : (
                      <p className="font-medium text-gray-900 mt-1">{customer?.phone || '-'}</p>
                    )}
                  </div>

                  {/* Data de Nascimento */}
                  <div>
                    <Label className="text-sm text-gray-600">Data de Nascimento</Label>
                    {isEditing ? (
                      <Input
                        value={editData.birth_date}
                        onChange={(e) => setEditData({ ...editData, birth_date: e.target.value })}
                        placeholder="DD/MM/AAAA"
                        className="mt-1"
                      />
                    ) : (
                      <p className="font-medium text-gray-900 mt-1">{customer?.birth_date || '-'}</p>
                    )}
                  </div>

                  {/* RG */}
                  <div>
                    <Label className="text-sm text-gray-600">RG</Label>
                    {isEditing ? (
                      <Input
                        value={editData.rg}
                        onChange={(e) => setEditData({ ...editData, rg: e.target.value })}
                        className="mt-1"
                      />
                    ) : (
                      <p className="font-medium text-gray-900 mt-1">{customer?.rg || '-'}</p>
                    )}
                  </div>

                  {/* Passaporte */}
                  <div>
                    <Label className="text-sm text-gray-600">
                      Passaporte
                      <span className="text-xs text-gray-400 ml-1">(opcional - para viagens aéreas)</span>
                    </Label>
                    {isEditing ? (
                      <Input
                        value={editData.passport}
                        onChange={(e) => setEditData({ ...editData, passport: e.target.value.toUpperCase() })}
                        placeholder="Ex: BR123456"
                        className="mt-1"
                      />
                    ) : (
                      <p className="font-medium text-gray-900 mt-1">{customer?.passport || '-'}</p>
                    )}
                  </div>

                  {/* Gênero */}
                  <div>
                    <Label className="text-sm text-gray-600">Gênero</Label>
                    {isEditing ? (
                      <select
                        value={editData.gender}
                        onChange={(e) => setEditData({ ...editData, gender: e.target.value })}
                        className="w-full mt-1 border border-gray-300 rounded-md px-3 py-2"
                      >
                        <option value="">Selecione</option>
                        <option value="masculino">Masculino</option>
                        <option value="feminino">Feminino</option>
                        <option value="outro">Outro</option>
                      </select>
                    ) : (
                      <p className="font-medium text-gray-900 mt-1 capitalize">{customer?.gender || '-'}</p>
                    )}
                  </div>
                </div>

                {/* Informação sobre alteração de senha */}
                <div className="mt-8 pt-6 border-t">
                  <h4 className="font-medium text-gray-900 mb-2">Alterar Senha</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Para alterar sua senha, utilize a opção &quot;Esqueci minha senha&quot; na tela de login.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => {
                      handleLogout();
                      setTimeout(() => navigate('/cliente/login'), 100);
                    }}
                  >
                    Ir para Login
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* PARCEIRO: Aba de Comissões */}
          {partnership.has_partnership && (
            <TabsContent value="comissoes">
              <div className="space-y-4">
                {/* Resumo */}
                {partnership.summary && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Card className="border-amber-200 bg-amber-50">
                      <CardContent className="p-4">
                        <p className="text-xs text-amber-700 font-semibold uppercase">Aguardando viagem</p>
                        <p className="text-2xl font-bold text-amber-900 mt-1" data-testid="commission-pending">
                          {new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format(partnership.summary.total_pending)}
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="border-emerald-200 bg-emerald-50">
                      <CardContent className="p-4">
                        <p className="text-xs text-emerald-700 font-semibold uppercase">Disponível a receber</p>
                        <p className="text-2xl font-bold text-emerald-900 mt-1" data-testid="commission-available">
                          {new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format(partnership.summary.total_available)}
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="border-gray-200 bg-gray-50">
                      <CardContent className="p-4">
                        <p className="text-xs text-gray-700 font-semibold uppercase">Já recebido</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1" data-testid="commission-paid">
                          {new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format(partnership.summary.total_paid)}
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Cupons do parceiro */}
                {partnership.coupons && partnership.coupons.length > 0 && (
                  <Card>
                    <CardContent className="p-4">
                      <h3 className="font-bold text-gray-900 mb-3">Meus Cupons</h3>
                      <div className="flex flex-wrap gap-2">
                        {partnership.coupons.map(c => (
                          <div key={c.code} className="px-3 py-2 rounded-lg bg-green-100 text-green-800 text-sm font-semibold">
                            {c.code} · {c.commission_percentage}% comissão
                            <span className="ml-2 text-xs opacity-70">({c.uses_count || 0} usos)</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Chave PIX de recebimento */}
                <Card className="border-emerald-200" data-testid="partner-pix-card">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-bold text-gray-900 flex items-center gap-2">
                        <Smartphone className="w-4 h-4 text-emerald-700" /> Chave PIX de Recebimento
                      </h3>
                      {!pixEditing && (
                        <Button variant="outline" size="sm" data-testid="partner-pix-edit" onClick={() => setPixEditing(true)}>
                          <Edit2 className="w-3 h-3 mr-1" /> {partnerPix.has_pix ? 'Editar' : 'Cadastrar'}
                        </Button>
                      )}
                    </div>
                    {pixMsg && <div className={`mb-3 p-2 rounded text-sm ${pixMsg.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>{pixMsg.text}</div>}
                    {!pixEditing ? (
                      partnerPix.has_pix ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                          <p><strong>Tipo:</strong> {partnerPix.pix_key_type?.toUpperCase()}</p>
                          <p><strong>Chave PIX:</strong> {partnerPix.pix_key}</p>
                          <p><strong>Titular:</strong> {partnerPix.pix_holder_name}</p>
                          {partnerPix.pix_holder_document && <p><strong>CPF/CNPJ:</strong> {partnerPix.pix_holder_document}</p>}
                        </div>
                      ) : (
                        <p className="text-sm text-amber-700">Você ainda não cadastrou uma chave PIX. Cadastre para receber suas comissões.</p>
                      )
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Tipo da chave</Label>
                          <select className="w-full border rounded p-2 text-sm" data-testid="partner-pix-type"
                            value={partnerPix.pix_key_type}
                            onChange={(e) => setPartnerPix({ ...partnerPix, pix_key_type: e.target.value })}>
                            <option value="cpf">CPF</option>
                            <option value="cnpj">CNPJ</option>
                            <option value="email">E-mail</option>
                            <option value="phone">Telefone</option>
                            <option value="random">Chave aleatória</option>
                          </select>
                        </div>
                        <div>
                          <Label className="text-xs">Chave PIX</Label>
                          <Input data-testid="partner-pix-key" value={partnerPix.pix_key} onChange={(e) => setPartnerPix({ ...partnerPix, pix_key: e.target.value })} />
                        </div>
                        <div>
                          <Label className="text-xs">Nome do titular</Label>
                          <Input data-testid="partner-pix-holder" value={partnerPix.pix_holder_name} onChange={(e) => setPartnerPix({ ...partnerPix, pix_holder_name: e.target.value })} />
                        </div>
                        <div>
                          <Label className="text-xs">CPF/CNPJ do titular (opcional)</Label>
                          <Input data-testid="partner-pix-doc" value={partnerPix.pix_holder_document || ''} onChange={(e) => setPartnerPix({ ...partnerPix, pix_holder_document: e.target.value })} />
                        </div>
                        <div className="sm:col-span-2 flex gap-2">
                          <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="partner-pix-save" onClick={savePartnerPix} disabled={pixSaving}>
                            <Save className="w-3 h-3 mr-1" /> {pixSaving ? 'Salvando…' : 'Salvar'}
                          </Button>
                          <Button variant="outline" onClick={() => { setPixEditing(false); setPixMsg(null); }}>Cancelar</Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Histórico de pagamentos recebidos */}
                {partnerPayments.length > 0 && (
                  <Card data-testid="partner-payments-card">
                    <CardContent className="p-4">
                      <h3 className="font-bold text-gray-900 mb-3">Meus Recebimentos</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="p-2 text-left">Data</th>
                              <th className="p-2 text-right">Valor</th>
                              <th className="p-2 text-center">Clientes</th>
                              <th className="p-2 text-center">Status</th>
                              <th className="p-2 text-center">Comprovante</th>
                            </tr>
                          </thead>
                          <tbody>
                            {partnerPayments.map(p => (
                              <tr key={p.id} className="border-b hover:bg-gray-50" data-testid={`partner-payment-${p.id}`}>
                                <td className="p-2 text-xs">{new Date(p.created_at).toLocaleString('pt-BR')}</td>
                                <td className="p-2 text-right font-bold text-emerald-700">{formatPrice(p.amount)}</td>
                                <td className="p-2 text-center">{(p.details || []).length}</td>
                                <td className="p-2 text-center">
                                  <span className={`text-xs px-2 py-1 rounded ${p.status === 'received' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                                    {p.status_label || (p.status === 'received' ? 'Recebido' : 'Pendente')}
                                  </span>
                                </td>
                                <td className="p-2 text-center">
                                  {p.status === 'received' && p.proof_file ? (
                                    <Button variant="outline" size="sm" data-testid={`partner-proof-${p.id}`} onClick={() => downloadPartnerProof(p.id)}>
                                      <Download className="w-3 h-3 mr-1" /> Baixar
                                    </Button>
                                  ) : (<span className="text-xs text-gray-400">—</span>)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Lista de comissões */}
                <Card>
                  <CardContent className="p-4">
                    <h3 className="font-bold text-gray-900 mb-3">Histórico de Comissões</h3>
                    {partnership.commissions.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-6">Nenhuma comissão registrada ainda. Compartilhe seu cupom!</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="p-2 text-left">Cliente</th>
                              <th className="p-2 text-left">Pacote</th>
                              <th className="p-2 text-left">Data Viagem</th>
                              <th className="p-2 text-left">Cupom</th>
                              <th className="p-2 text-right">Valor Pago</th>
                              <th className="p-2 text-right">Comissão</th>
                              <th className="p-2 text-center">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {partnership.commissions.map(c => {
                              const statusLabel = c.status_label
                                || (c.status === 'paid' ? 'Comissão paga'
                                  : c.status === 'available' ? 'Comissão liberada'
                                  : c.status === 'cancelled_agency_refunded' ? 'Viagem cancelada — cliente reembolsado'
                                  : c.status === 'cancelled_customer' ? 'Cancelado — cliente desistiu'
                                  : c.status === 'postponed' ? 'Viagem adiada'
                                  : 'Aguardando viagem');
                              const statusClass = c.status === 'paid' ? 'bg-gray-100 text-gray-700'
                                : c.status === 'available' ? 'bg-emerald-100 text-emerald-800'
                                : c.status === 'cancelled_agency_refunded' ? 'bg-orange-100 text-orange-800'
                                : c.status === 'cancelled_customer' ? 'bg-red-100 text-red-800'
                                : c.status === 'postponed' ? 'bg-purple-100 text-purple-800'
                                : 'bg-amber-100 text-amber-800';
                              return (
                                <tr key={c.reservation_id} className="border-b hover:bg-gray-50" data-testid={`commission-row-${c.status}`}>
                                  <td className="p-2 uppercase">{c.customer_name || '-'}</td>
                                  <td className="p-2">
                                    {c.previous_package_snapshot && (
                                      <div className="text-[11px] mb-1 space-y-0.5" data-testid="postponed-info">
                                        <p className="text-red-600 font-bold">NOVA VIAGEM</p>
                                        <p className="text-red-700">Novo pacote: {c.package_name}</p>
                                        <p className="text-red-700">Nova data: {c.selected_date}</p>
                                        <p className="text-gray-500 line-through">Anterior: {c.previous_package_snapshot.package_name} — {c.previous_package_snapshot.selected_date}</p>
                                      </div>
                                    )}
                                    {!c.previous_package_snapshot && (c.package_name || '-')}
                                  </td>
                                  <td className="p-2">{!c.previous_package_snapshot ? (c.selected_date || '-') : '—'}</td>
                                  <td className="p-2 font-mono">{c.coupon_code}</td>
                                  <td className="p-2 text-right">{new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format(c.paid_by_client)}</td>
                                  <td className="p-2 text-right font-bold text-emerald-700">{new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format(c.commission_value)}</td>
                                  <td className="p-2 text-center">
                                    <span className={`text-xs px-2 py-1 rounded ${statusClass}`}>{statusLabel}</span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-3 italic">
                      A comissão é liberada 7 dias após o fim da viagem, para dar tempo de eventuais cancelamentos serem processados.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Trip Details Modal */}
      <Dialog open={!!selectedTrip} onOpenChange={() => setSelectedTrip(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {isAirTravel(selectedTrip?.package_category) ? (
                <Plane className="w-5 h-5 text-blue-600" />
              ) : (
                <Bus className="w-5 h-5 text-green-600" />
              )}
              {selectedTrip?.package_name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedTrip && (
            <div className="space-y-6">
              {/* Status e Data */}
              <div className="flex items-center justify-between">
                {getStatusBadge(selectedTrip.status, selectedTrip.payment_status)}
                <span className="text-gray-600">{selectedTrip.selected_date}</span>
              </div>
              
              {/* Informações Básicas */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-xs text-gray-500">Quantidade</p>
                  <p className="font-medium">{selectedTrip.number_of_people || 1} pessoa(s)</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Acomodação</p>
                  <p className="font-medium">{getRoomTypeLabel(selectedTrip.room_type)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Pagamento</p>
                  <p className="font-medium">{getPaymentMethodLabel(selectedTrip.payment_method)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Valor Total</p>
                  <p className="font-bold text-[#003366]">{formatPrice(selectedTrip.total_amount)}</p>
                </div>
              </div>

              {/* Ponto de Embarque / Cidade de Embarque / Sem Aéreo */}
              {selectedTrip.transport_mode === 'without_air' ? (
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200" data-testid="trip-without-air">
                  <div className="flex items-center gap-2 mb-1">
                    <MapPin className="w-5 h-5 text-amber-700" />
                    <h4 className="font-semibold text-amber-900">Transporte</h4>
                  </div>
                  <p className="text-amber-800 font-medium">Aéreo não contratado</p>
                  <p className="text-xs text-amber-700 mt-1">Você é responsável pelo próprio deslocamento até o destino.</p>
                </div>
              ) : (selectedTrip.boarding_point || selectedTrip.city_departure) && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2 mb-1">
                    <MapPin className="w-5 h-5 text-blue-600" />
                    <h4 className="font-semibold text-blue-900">
                      {isAirTravel(selectedTrip.package_category) ? 'Cidade de Embarque' : 'Ponto de Embarque'}
                    </h4>
                  </div>
                  <p className="text-blue-800">
                    {isAirTravel(selectedTrip.package_category) 
                      ? (selectedTrip.city_departure === 'RJ' ? 'Rio de Janeiro (GIG)' : 
                         selectedTrip.city_departure === 'SP' ? 'São Paulo (GRU)' : 
                         selectedTrip.city_departure || selectedTrip.boarding_point)
                      : selectedTrip.boarding_point
                    }
                  </p>
                </div>
              )}

              {/* Acompanhantes — substituição/solicitação de troca */}
              {(() => {
                const travelers = selectedTrip.travelers || selectedTrip.all_travelers || [];
                if (travelers.length <= 1) return null;
                const cat = (selectedTrip.package_category || '').toLowerCase();
                const isRodo = cat.includes('rodoviario') || cat.includes('rodoviário');
                return (
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200" data-testid="companions-section">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="w-5 h-5 text-gray-700" />
                      <h4 className="font-semibold text-gray-900">Acompanhantes ({travelers.length - 1})</h4>
                    </div>
                    <ul className="text-sm space-y-1 mb-3">
                      {travelers.slice(1).map((t, i) => (
                        <li key={i} className="flex justify-between items-center border-b border-gray-100 py-1">
                          <span>{t.full_name || t.name || `Acompanhante ${i+1}`}</span>
                          <Button size="sm" variant="outline" data-testid={`replace-companion-btn-${i+1}`}
                            onClick={() => {
                              const full_name = window.prompt('Nome completo do novo participante:');
                              if (!full_name) return;
                              const cpf = window.prompt('CPF do novo participante (só números):');
                              if (!cpf) return;
                              const birth_date_raw = window.prompt('Data de nascimento (DD/MM/AAAA):');
                              const birth_date = maskBRDate(birth_date_raw || '');
                              if (birth_date && !isValidBRDate(birth_date)) {
                                alert('Data de nascimento inválida. Use o formato DD/MM/AAAA.');
                                return;
                              }
                              const payload = {
                                reservation_id: selectedTrip.id,
                                customer_cpf: customer?.cpf || '',
                                old_traveler_index: i + 1,
                                new_traveler: { full_name, cpf, birth_date: birth_date || null },
                                reason: isRodo ? null : (window.prompt('Motivo (opcional):') || null),
                              };
                              const url = isRodo ? '/api/reservations-ops/replace-companion' : '/api/reservations-ops/request-companion-change';
                              axios.post(`${BACKEND_URL}${url}`, payload)
                                .then((res) => {
                                  alert(res.data?.message || (isRodo ? 'Acompanhante substituído com sucesso.' : 'Solicitação registrada.'));
                                  loadReservations(customer?.cpf);
                                  setSelectedTrip(null);
                                })
                                .catch(e => alert(e.response?.data?.detail || 'Erro ao processar.'));
                            }}
                          >
                            {isRodo ? 'Substituir' : 'Solicitar troca'}
                          </Button>
                        </li>
                      ))}
                    </ul>
                    <p className="text-[11px] text-gray-500">
                      {isRodo
                        ? 'Você pode substituir acompanhantes diretamente. A quantidade de pessoas da reserva permanece igual e não há nova cobrança pela substituição.'
                        : 'Para viagens aéreas, a substituição precisa ser aprovada pela agência (passagens podem já estar emitidas).'}
                    </p>
                    {/* Histórico de substituições */}
                    {selectedTrip.companion_history && selectedTrip.companion_history.length > 0 && (
                      <details className="mt-3" data-testid="companion-history">
                        <summary className="cursor-pointer text-xs text-gray-600">Histórico de substituições ({selectedTrip.companion_history.length})</summary>
                        <ul className="mt-2 space-y-1 text-xs text-gray-600">
                          {selectedTrip.companion_history.map((h, idx) => (
                            <li key={idx} className="border-l-2 border-gray-300 pl-2">
                              <div className="text-[10px]">{new Date(h.at).toLocaleString('pt-BR')}</div>
                              <div>
                                <strong>{h.type === 'companion_replacement' ? 'Substituição' : (h.status === 'approved' ? 'Aprovada' : h.status === 'denied' ? 'Recusada' : 'Solicitação')}</strong>: {h.old_traveler?.full_name || '-'} → {h.new_traveler?.full_name || '-'}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                );
              })()}
              
              {/* Documentação de MENORES */}
              {(() => {
                const travelers = selectedTrip.travelers || selectedTrip.all_travelers || [];
                const tripStr = selectedTrip.selected_date || '';
                const m = tripStr.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
                if (!m) return null;
                const tripD = new Date(parseInt(m[3],10), parseInt(m[2],10)-1, parseInt(m[1],10));
                const isMinor = (t) => {
                  const bd = t.birth_date || t.birthDate;
                  if (!bd) return false;
                  const mm = String(bd).match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
                  if (!mm) return false;
                  const birthD = new Date(parseInt(mm[3],10), parseInt(mm[2],10)-1, parseInt(mm[1],10));
                  let age = tripD.getFullYear() - birthD.getFullYear();
                  if (tripD.getMonth() < birthD.getMonth() || (tripD.getMonth() === birthD.getMonth() && tripD.getDate() < birthD.getDate())) age -= 1;
                  return age < 18;
                };
                const minors = travelers.map((t, i) => isMinor(t) ? { t, i } : null).filter(Boolean);
                if (minors.length === 0) return null;
                const docs = selectedTrip.minor_documents || [];
                return (
                  <div className="pt-4 border-t" data-testid="minor-docs-section">
                    <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-purple-700" /> Documentação de Menores
                    </h4>
                    <p className="text-xs text-gray-600 mb-3">Anexe os documentos exigidos abaixo. Nossa equipe analisará e retornará com aprovação/recusa.</p>
                    <div className="space-y-3">
                      {minors.map(({ t, i }) => {
                        const dset = docs.filter(d => d.traveler_index === i);
                        return (
                          <div key={i} className="p-3 bg-purple-50 border border-purple-200 rounded" data-testid={`minor-docs-t-${i}`}>
                            <p className="font-semibold text-sm text-purple-900 mb-2">{t.full_name || t.name || `Viajante ${i+1}`}</p>
                            {dset.length > 0 ? (
                              <div className="space-y-1 mb-2">
                                {dset.map(d => (
                                  <div key={d.id} className="flex items-center justify-between text-xs bg-white p-2 rounded" data-testid={`minor-doc-${d.id}`}>
                                    <div>
                                      <span className="font-medium">{d.document_type}</span> · {d.filename}
                                    </div>
                                    <span className={`px-2 py-0.5 rounded ${
                                      d.status === 'approved' ? 'bg-emerald-100 text-emerald-800' :
                                      d.status === 'denied' ? 'bg-red-100 text-red-800' :
                                      d.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                                      'bg-amber-100 text-amber-800'}`}>{d.status_label || d.status}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-xs text-amber-700 mb-2">Nenhum documento enviado ainda.</p>
                            )}
                            <div className="flex flex-wrap gap-2 items-center">
                              <select id={`minor-doctype-${i}`} className="text-xs border rounded p-1" data-testid={`minor-doctype-${i}`}>
                                <option value="identification">Documento de identidade</option>
                                <option value="birth_cert">Certidão de nascimento</option>
                                <option value="kinship_proof">Comprovante de parentesco</option>
                                <option value="travel_authorization">Autorização de viagem</option>
                                <option value="responsible_doc">Doc. do responsável</option>
                                <option value="other">Outro</option>
                              </select>
                              <label className="text-xs cursor-pointer bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded">
                                <span>Anexar documento</span>
                                <input type="file" className="hidden" data-testid={`minor-upload-${i}`}
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    const docType = document.getElementById(`minor-doctype-${i}`)?.value || 'other';
                                    const reader = new FileReader();
                                    reader.onload = async () => {
                                      try {
                                        await axios.post(`${BACKEND_URL}/api/minors/attach-document`, {
                                          reservation_id: selectedTrip.id,
                                          traveler_index: i,
                                          document_type: docType,
                                          file_base64: reader.result,
                                          filename: file.name,
                                        });
                                        alert('Documento enviado. Aguardando análise.');
                                        // recarrega reservas
                                        loadReservations(customer.cpf);
                                        setSelectedTrip(null);
                                      } catch (err) { alert(err.response?.data?.detail || 'Erro ao enviar.'); }
                                    };
                                    reader.readAsDataURL(file);
                                  }} />
                              </label>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
              
              {/* Roteiro Dia a Dia */}
              {selectedTrip.itinerary && selectedTrip.itinerary.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-[#003366]" />
                    Roteiro
                  </h4>
                  <div className="space-y-3">
                    {selectedTrip.itinerary.map((day, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded-lg border-l-4 border-[#003366]">
                        <p className="font-semibold text-gray-900">{day.day || `Dia ${idx + 1}`}</p>
                        <p className="text-sm text-gray-600 mt-1">{day.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* O que está incluso */}
              {selectedTrip.package_includes && selectedTrip.package_includes.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">✓ O que está incluso:</h4>
                  <ul className="space-y-1 text-sm text-gray-600">
                    {selectedTrip.package_includes.map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle2 className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* O que NÃO está incluso */}
              {selectedTrip.package_not_includes && selectedTrip.package_not_includes.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">✗ Não incluso:</h4>
                  <ul className="space-y-1 text-sm text-gray-500">
                    {selectedTrip.package_not_includes.map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <X className="w-4 h-4 text-red-400 mr-2 mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Botão WhatsApp - Suporte contextual */}
              <Button
                onClick={() => {
                  const phone = "5521999999999"; // Número do WhatsApp da agência
                  const message = encodeURIComponent(
                    `Olá! Tenho uma dúvida sobre minha viagem:\n\n` +
                    `📦 Pacote: ${selectedTrip.package_name}\n` +
                    `📅 Data: ${selectedTrip.selected_date}\n` +
                    `👤 Nome: ${customer?.full_name || 'Cliente'}\n` +
                    `📋 Reserva ID: ${selectedTrip.id}\n\n` +
                    `Minha dúvida é:`
                  );
                  window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
                }}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-3"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Tirar dúvidas sobre essa viagem
              </Button>

              {/* Botão Ver Contrato se disponível */}
              {selectedTrip.contract_id && (
                <Button
                  variant="outline"
                  onClick={() => navigate(`/contrato/${selectedTrip.id}`)}
                  className="w-full border-[#003366] text-[#003366]"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Ver Contrato
                </Button>
              )}

              {/* Novo contrato disponível após transferência */}
              {selectedTrip.contract_needs_new_acceptance && (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm" data-testid="new-contract-notice">
                  <p className="font-bold mb-1">⚠️ NOVO CONTRATO DISPONÍVEL PARA ACEITE</p>
                  <p className="text-xs">Sua reserva foi transferida. Por favor, aceite o novo contrato para confirmar a nova contratação.</p>
                </div>
              )}

              {/* Solicitar cancelamento (não cancela — apenas registra) */}
              {!['cancelled_by_agency','cancelled_by_customer','cancellation_requested','cancelled_transferred_to_credit'].includes(selectedTrip.status || '') && (
                <Button
                  variant="outline"
                  className="w-full border-red-500 text-red-600 hover:bg-red-50"
                  data-testid="request-cancellation-btn"
                  onClick={async () => {
                    if (!window.confirm('Deseja solicitar o cancelamento desta reserva? Nossa equipe entrará em contato em até 48 horas.')) return;
                    try {
                      const res = await axios.post(`${BACKEND_URL}/api/reservations-ops/customer-cancel-request`, {
                        reservation_id: selectedTrip.id,
                        customer_cpf: customer?.cpf || '',
                        reason: window.prompt('Motivo (opcional):') || null,
                      });
                      alert(res.data?.message || 'Solicitação registrada.');
                      setSelectedTrip(null);
                      loadReservations(customer?.cpf);
                    } catch (e) {
                      alert(e.response?.data?.detail || 'Erro ao registrar solicitação.');
                    }
                  }}
                >
                  <AlertCircle className="w-4 h-4 mr-2" />
                  Solicitar cancelamento da viagem
                </Button>
              )}
              {selectedTrip.status === 'cancellation_requested' && (
                <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-yellow-900 text-sm" data-testid="cancel-request-notice">
                  ⏳ <strong>Cancelamento solicitado</strong> — aguardando análise da agência.
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerDashboard;
